/* eslint-disable no-unused-vars */
import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ButtonField from '../../Components/Ui/ButtonField';
import { useTailwindBrand } from '../../Components/shared';

interface StepShellProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    children: React.ReactNode;
    onPrev?: () => void;
    onNext?: () => void;
    nextLabel?: string;
    prevLabel?: string;
    nextLoading?: boolean;
    nextDisabled?: boolean;
    /** Affiche une coche au lieu d'une flèche sur le bouton suivant (dernière étape). */
    isFinal?: boolean;
    /** Affiche la mention « * Champs obligatoires » en bas du formulaire (étapes de saisie). */
    requiredLegend?: boolean;
}

/**
 * Coquille commune à chaque étape : titre, sous-titre, contenu et pied de page
 * avec les boutons Précédent / Suivant. Reprend le rôle de DeclarationCourse/IndexStep
 * mais sans dépendance Redux (la navigation est fournie par le contexte AICI).
 */
const StepShell = ({
    title,
    subtitle,
    children,
    onPrev,
    onNext,
    nextLabel = 'Suivant',
    prevLabel = 'Précédent',
    nextLoading = false,
    nextDisabled = false,
    isFinal = false,
    requiredLegend = false,
}: StepShellProps) => {
    const { classes } = useTailwindBrand();

    return (
        <div className="flex flex-col">
            <h2 className={`text-xl lg:text-2xl font-extrabold ${classes.primaryText} ${classes.fontFamily}`}>
                {title}
            </h2>
            {subtitle && (
                <p className={`mt-2 text-sm lg:text-base ${classes.text} ${classes.fontFamily}`}>{subtitle}</p>
            )}

            <div className="mt-6">{children}</div>

            {requiredLegend && (
                <p className={`mt-4 text-xs italic ${classes.text} ${classes.fontFamily}`}>
                    <span className="text-red-700">*</span> Champs obligatoires
                </p>
            )}

            {(onPrev || onNext) && (
                <div className="flex flex-col-reverse gap-3 pt-8 mt-8 border-t border-gray-100 lg:flex-row lg:justify-between">
                    <div className="w-full lg:w-auto">
                        {onPrev && (
                            <ButtonField
                                label={prevLabel}
                                variant="outlined"
                                className="w-full font-bold lg:w-auto"
                                startIcon={<ArrowBackIcon />}
                                disabled={nextLoading}
                                onClick={onPrev}
                            />
                        )}
                    </div>
                    <div className="w-full lg:w-auto">
                        {onNext && (
                            <ButtonField
                                label={nextLabel}
                                variant="contained"
                                className="w-full font-bold lg:w-auto"
                                endIcon={isFinal ? <CheckCircleOutlineIcon /> : <ArrowForwardIcon />}
                                loading={nextLoading}
                                disabled={nextDisabled}
                                onClick={onNext}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StepShell;
